import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { CameraStateContext, CameraStateDispatchContext } from 'context/state/camera';

import { CameraControlDeck, CameraControlDeckType } from 'components/camera/CameraControlDeck';
import { CameraVideoOverlay } from 'components/camera/CameraVideoOverlay';
import { CameraVideoPlaceholder } from 'components/camera/CameraVideoPlaceholder';
import { BatteryIndicator } from 'components/camera/indicators/BatteryIndicator';
import { LiveIndicator } from 'components/camera/indicators/LiveIndicator';
import { RecordingIndicator } from 'components/camera/indicators/RecordingIndicator';
import { useVideoScreenshot } from 'hooks/useVideoScreenshot';
import { useRecording } from 'hooks/useRecording';
import { useWebRTC } from 'hooks/useWebRTC';
import { subscribe, unsubscribe } from 'modules/globalEvents';

import { AppDeviceLeaf } from 'types/common/leaf';

import styles from './cameraVideo.module.scss';

type CameraVideoProps = {
  leaf: AppDeviceLeaf;
  controlDeckType?: CameraControlDeckType;
  isFocusedView?: boolean;
};

export const CameraVideo = ({ leaf, controlDeckType, isFocusedView }: CameraVideoProps) => {
  const { fullscreenEnabled, soundEnabled, recording } = useContext(CameraStateContext);
  const dispatch = useContext(CameraStateDispatchContext);

  const [showControls, setShowControls] = useState(false);
  const showControlsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const videoPlayerRef = useRef<HTMLDivElement>(null);
  const screenshotCanvasRef = useRef<HTMLCanvasElement>(null);

  const { connect, remoteStream, rtcConnection, rtcConnectionState, rtcLoading, rtcError } = useWebRTC({
    leaf,
  });

  const { takeScreenshot, downloadScreenshot } = useVideoScreenshot({
    leaf,
    videoRef,
    canvasRef: screenshotCanvasRef,
  });

  const { startRecording, stopRecording } = useRecording({ leaf });

  const isOverlayVisible = rtcConnectionState !== 'connected' || !remoteStream;
  const isPlaceholderVisible = !(remoteStream && rtcConnectionState === 'connected');
  const isLive = rtcConnectionState === 'connected' && remoteStream?.active;
  const batteryLevel = leaf?.state?.battery?.level;

  // Make sure to close PIP when the component is unmounted
  useEffect(
    () => () => {
      const pipElement = document.pictureInPictureElement as HTMLVideoElement;
      if (pipElement) {
        pipElement.pause();
        document.exitPictureInPicture();
      }
    },
    [],
  );

  // Subscribe to TOGGLE_FULLSCREEN event and unsubscribe on component unmount
  useEffect(() => {
    if (videoPlayerRef.current) {
      subscribe(leaf.id, 'TOGGLE_FULLSCREEN', ({ detail }) => {
        if (videoPlayerRef.current) {
          if (detail.leafId === leaf.id) {
            if (detail.data.newValue) {
              videoPlayerRef.current.requestFullscreen();
            } else {
              document.exitFullscreen();
            }
          }
        }
      });
      videoPlayerRef.current.onfullscreenchange = () => {
        if (document.fullscreenElement) {
          dispatch({ type: 'fullscreen', newValue: true });
        } else {
          dispatch({ type: 'fullscreen', newValue: false });
        }
      };
    }

    return () => {
      unsubscribe(leaf.id, 'TOGGLE_FULLSCREEN');
    };
  }, [leaf.id]);

  // Subscribe to TOGGLE_SOUND and TOGGLE_PIP events and unsubscribe on component unmount
  useEffect(() => {
    if (videoRef.current) {
      subscribe(leaf.id, 'TOGGLE_SOUND', ({ detail }) => {
        if (videoRef.current) {
          if (detail.leafId === leaf.id) {
            if (detail.data.newValue) {
              videoRef.current.muted = false;
            } else {
              videoRef.current.muted = true;
            }
            dispatch({ type: 'sound', newValue: detail.data.newValue });
          } else {
            videoRef.current.muted = true;
            dispatch({ type: 'sound', newValue: false });
          }
        }
      });

      subscribe(leaf.id, 'TOGGLE_PIP', ({ detail }) => {
        if (videoRef.current && detail.leafId === leaf.id) {
          if (detail.data.newValue) {
            videoRef.current.requestPictureInPicture();
          } else {
            document.exitPictureInPicture();
          }
        }
      });
      videoRef.current.onenterpictureinpicture = () => {
        dispatch({ type: 'pip', newValue: true });
      };
      videoRef.current.onleavepictureinpicture = () => {
        dispatch({ type: 'pip', newValue: false });
      };
    }

    return () => {
      unsubscribe(leaf.id, 'TOGGLE_SOUND');
      unsubscribe(leaf.id, 'TOGGLE_PIP');
    };
  }, [leaf.id]);

  // Set the video source when remoteStream changes
  useEffect(() => {
    if (videoRef.current && remoteStream) {
      videoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // Subscribe to TAKE_SCREENSHOT event and unsubscribe on component unmount
  useEffect(() => {
    if (videoRef.current && screenshotCanvasRef.current) {
      subscribe(leaf.id, 'TAKE_SCREENSHOT', ({ detail }) => {
        if (detail?.leafId === leaf.id) {
          takeScreenshot();
          downloadScreenshot(leaf.name);
        }
      });
    }

    return () => {
      unsubscribe(leaf.id, 'TAKE_SCREENSHOT');
    };
  }, [leaf.id, videoRef.current, screenshotCanvasRef.current]);

  // Subscribe to TOGGLE_RECORDING event and unsubscribe on component unmount
  useEffect(() => {
    if (remoteStream) {
      subscribe(leaf.id, 'TOGGLE_RECORDING', async ({ detail }) => {
        if (detail.leafId === leaf.id) {
          if (detail.data.newValue) {
            const { recorder, startTime } = await startRecording(remoteStream);
            dispatch({
              type: 'recording',
              payload: {
                recordingEnabled: true,
                startTime,
                recorder,
              },
            });
          } else {
            const { recorder } = recording;
            if (recorder) {
              stopRecording(recorder);
              dispatch({
                type: 'recording',
                payload: {
                  recordingEnabled: false,
                  startTime: null,
                  recorder: null,
                },
              });
            }
          }
        }
      });
    }

    return () => {
      unsubscribe(leaf.id, 'TOGGLE_RECORDING');

      const { recorder } = recording;
      if (recorder) {
        stopRecording(recorder);
        dispatch({
          type: 'recording',
          payload: {
            recordingEnabled: false,
            startTime: null,
            recorder: null,
          },
        });
      }
    };
  }, [leaf.id, recording.recorder, remoteStream]);

  return (
    <Box
      id={`videoContainer-${leaf.id}`}
      className={`${styles.videoContainer} ${fullscreenEnabled ? styles.fullscreen : ''}  ${isFocusedView ? styles.focused : ''}`}
      data-testid="component-camera-video"
      ref={videoPlayerRef}
      onMouseEnter={() => {
        setShowControls(true);
      }}
      onMouseLeave={() => {
        setShowControls(false);
      }}
      onMouseMove={() => {
        if (fullscreenEnabled) {
          if (showControlsTimeout.current) {
            clearTimeout(showControlsTimeout.current);
          }
          setShowControls(true);
          showControlsTimeout.current = setTimeout(() => {
            setShowControls(false);
          }, 3000);
        }
      }}
    >
      {isOverlayVisible && (
        <CameraVideoOverlay
          leaf={leaf}
          connect={connect}
          rtcConnection={rtcConnection}
          rtcLoading={rtcLoading}
          rtcError={rtcError}
        />
      )}
      {isPlaceholderVisible && (
        <CameraVideoPlaceholder
          leaf={leaf}
          cameraVideoPlaceholderType="normal"
          isBatteryTimeout={!!rtcConnection?.batteryTimeout}
        />
      )}
      <Box className={isPlaceholderVisible ? `${styles.videoElement} ${styles.hidden}` : styles.videoElement}>
        <Box className={styles.videoWrapper}>
          {isLive && <LiveIndicator />}
          {recording.recordingEnabled && <RecordingIndicator />}
          <video ref={videoRef} id={`video-${leaf.id}`} autoPlay muted={!soundEnabled} controls={false} />
        </Box>
        <canvas ref={screenshotCanvasRef} />

        <Box className={`${styles.videoOverlay} ${showControls && styles.visible}`}>
          <Stack className={styles.videoHeaderContainer}>
            {fullscreenEnabled && <Typography variant="h5">{leaf.name}</Typography>}
            {batteryLevel !== undefined && batteryLevel >= 0 && <BatteryIndicator batteryLevel={batteryLevel} />}
          </Stack>
          <Box
            className={styles.videoControlsContainer}
            onBlur={() => {
              if (fullscreenEnabled) {
                setShowControls(false);
              }
            }}
            onMouseMove={(event) => {
              event.stopPropagation();
              if (fullscreenEnabled && showControlsTimeout.current) {
                clearTimeout(showControlsTimeout.current);
              }
            }}
          >
            <CameraControlDeck leaf={leaf} controlDeckType={controlDeckType} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
